import { useCallback, useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Button as AriaDisclosureButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { useToast } from "@/components/application/notifications/toast-provider";

/**
 * UC-004 QrDisplay — mobile-first SPAYD QR wrapper (Phase 19, REQ-UI-009 +
 * REQ-UI-018 + REQ-BIZ-017..019 + REQ-PERF-004).
 *
 * Renderuje PNG QR (base64 z backend `qr_code.png_blob`) jako `<img>` s
 * pinch-to-zoom gesture handling, "Stáhnout PNG" download trigger a
 * Disclosure "Manuální údaje" panelem s 3× Copy buttons.
 *
 * EUR-only měsíc (princip #2 source-of-truth + REQ-BIZ-018):
 *   - `pngBase64 = null` → renderujeme Alert s EUR advisory, žádný QR
 *     (princip #2 — žádný CZK fallback s FX přepočtem). Drill-down kalkulace
 *     v rodičovské stránce ukazuje EUR řádek.
 *
 * Pinch-to-zoom (REQ-UI-018 + NFR-004 mobile-first):
 *   - 2 souběžné pointerdown události na image container → tracking 2 prstů.
 *   - Pri každém pointermove vypočítáme novou distance / initial distance ratio
 *     a aplikujeme `transform: scale(N)` (clamp 1.0 ≤ N ≤ 3.0).
 *   - Single-pointer (touch nebo myš) → no-op (žádné scrollování / pan).
 *   - Test simuluje 2 `pointerdown` + 1 `pointermove` (větší distance) →
 *     scale ≥ 2 (REQ-UI-018 + DoD verifikace).
 *
 * Web Share API NEimplementováno (post-MVP per REQ-BIZ-043 +
 * `spec/docs/10-parking-lot.md`). MVP používá pouze "Stáhnout PNG" +
 * "Copy" buttons na manuální údaje. Web Share API funkce se nevolají
 * (grep gate v phase 19 DoD enforce-uje).
 *
 * Color-not-only (REQ-UI-022 §5.9.2):
 *   - Toast feedback po Copy obsahuje text "Zkopírováno" + ✓ symbol; Alert
 *     pro EUR-only obsahuje "ⓘ" leading.
 */

export interface QrDisplayProps {
  /**
   * SPAYD PNG QR jako base64 string (bez `data:image/png;base64,` prefix).
   * `null` → EUR-only měsíc, render advisory Alert místo QR.
   */
  pngBase64: string | null;
  /** Czech IBAN (CZxx + 22 digits) pro manuální zadání. */
  iban: string;
  /** Variabilní symbol (digits-only). */
  variableSymbol: string;
  /** Částka v Kč (plain decimal string z backendu — žádný FX přepočet). */
  amountKc: string;
  /**
   * UC-003 SPAYD MSG (sanitized ASCII) — zobrazí se ve Disclosure pro
   * uživatele, který chce ověřit "Z čeho se částka skládá".
   */
  message?: string;
  /**
   * Optional zóna / billing group code — pro advisory Alert text
   * (EUR-only edge case).
   */
  billingGroupCode?: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 3;

interface PointerSnap {
  id: number;
  x: number;
  y: number;
}

function distance(a: PointerSnap, b: PointerSnap): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Helper — base64 → Blob pro download trigger.
 * Nepoužívá fetch (data: URL); manual atob + Uint8Array konverze.
 */
function base64ToPngBlob(base64: string): Blob {
  const bytes = atob(base64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    buf[i] = bytes.charCodeAt(i);
  }
  return new Blob([buf], { type: "image/png" });
}

export function QrDisplay(props: QrDisplayProps) {
  const { pngBase64, iban, variableSymbol, amountKc, message, billingGroupCode } = props;
  const { t } = useTranslation("tenant");
  const { push } = useToast();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointersRef = useRef<Map<number, PointerSnap>>(new Map());
  const initialDistanceRef = useRef<number | null>(null);
  const [scale, setScale] = useState<number>(1);

  // EUR-only — render advisory Alert namísto QR.
  if (pngBase64 === null) {
    return (
      <div data-testid="qr-display-eur-only">
        <InlineAlert variant="info" title={t("payment.eurOnly.title")}>
          <p>
            {t("payment.eurOnly.body", { code: billingGroupCode ?? "" })}
          </p>
          <p>
            <strong>{t("payment.eurOnly.note")}</strong>
          </p>
        </InlineAlert>
      </div>
    );
  }

  const dataUrl = `data:image/png;base64,${pngBase64}`;

  const handlePointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    pointersRef.current.set(e.pointerId, { id: e.pointerId, x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [p1, p2] = Array.from(pointersRef.current.values());
      if (p1 && p2) {
        initialDistanceRef.current = distance(p1, p2);
      }
    }
    // Capture pointer so we keep receiving moves even when outside element.
    try {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // jsdom & older browsers may not support — ignore.
    }
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const existing = pointersRef.current.get(e.pointerId);
    if (!existing) return;
    pointersRef.current.set(e.pointerId, { id: e.pointerId, x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && initialDistanceRef.current && initialDistanceRef.current > 0) {
      const [p1, p2] = Array.from(pointersRef.current.values());
      if (p1 && p2) {
        const current = distance(p1, p2);
        const next = clamp(current / initialDistanceRef.current, MIN_SCALE, MAX_SCALE);
        setScale(next);
      }
    }
  }, []);

  const handlePointerEnd = useCallback((e: PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      initialDistanceRef.current = null;
    }
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    initialDistanceRef.current = null;
  }, []);

  const handleDownload = useCallback(() => {
    if (typeof window === "undefined" || !pngBase64) return;
    try {
      const blob = base64ToPngBlob(pngBase64);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr-platba-${variableSymbol}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      push({ variant: "success", message: `✓ ${t("payment.qrDownload.toast")}` });
    } catch {
      push({ variant: "error", message: t("payment.qrDownload.failed") });
    }
  }, [pngBase64, variableSymbol, push, t]);

  const handleCopy = useCallback(
    async (value: string, fieldLabel: string) => {
      const successMsg = `✓ ${fieldLabel}: ${t("payment.copy.toast")}`;
      const failMsg = t("payment.copy.failed");
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          push({ variant: "success", message: successMsg });
          return;
        }
        // Fallback (jsdom + older browsers): execCommand("copy")
        if (typeof document !== "undefined") {
          const ta = document.createElement("textarea");
          ta.value = value;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          // execCommand může vyhodit / vrátit false v jsdom — chytíme níže.
          const ok = (document as unknown as { execCommand?: (cmd: string) => boolean })
            .execCommand?.("copy");
          document.body.removeChild(ta);
          if (ok) {
            push({ variant: "success", message: successMsg });
            return;
          }
        }
        push({ variant: "error", message: failMsg });
      } catch {
        push({ variant: "error", message: failMsg });
      }
    },
    [push, t],
  );

  return (
    <section
      className="flex flex-col gap-4 rounded-xl bg-primary p-4 ring-1 ring-inset ring-secondary"
      data-testid="qr-display"
    >
      <div
        ref={containerRef}
        className="flex min-h-[280px] touch-none items-center justify-center overflow-hidden rounded-lg bg-white p-2 select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        data-testid="qr-display-viewport"
        style={{ touchAction: "none" }}
      >
        <img
          src={dataUrl}
          alt={t("payment.qrTitle")}
          aria-label={t("payment.qrAriaLabel")}
          className="block h-[260px] w-[260px] max-w-full object-contain will-change-transform"
          data-testid="qr-display-img"
          data-scale={scale.toFixed(2)}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            transition: pointersRef.current.size === 2 ? "none" : "transform 120ms ease-out",
          }}
          draggable={false}
        />
      </div>
      <p className="m-0 text-center text-sm text-tertiary">{t("payment.qrInstructions")}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          type="button"
          color="primary"
          onPress={handleDownload}
          data-testid="qr-display-download"
        >
          {t("payment.qrDownload.label")}
        </Button>
        {scale > 1 && (
          <Button
            type="button"
            color="tertiary"
            onPress={handleReset}
            data-testid="qr-display-reset-zoom"
          >
            {t("payment.qrResetZoom.label")}
          </Button>
        )}
      </div>
      {/* Disclosure inline přes react-aria-components (legacy ../ui barrel se ruší).
          Zachovává aria-expanded toggle button + collapsible panel; vizuál na UUI
          tokenech (ring-1 ring-secondary, rounded-xl, bg-primary). */}
      <AriaDisclosure className="overflow-hidden rounded-xl bg-primary ring-1 ring-secondary">
        {({ isExpanded }) => (
          <>
            <Heading className="m-0">
              <AriaDisclosureButton
                slot="trigger"
                className="flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-transparent px-4 py-3.5 text-left text-md font-medium text-primary outline-focus-ring focus-visible:outline-2 focus-visible:-outline-offset-2"
              >
                <span>{t("payment.manualDetails.title")}</span>
                <span aria-hidden="true">{isExpanded ? "−" : "+"}</span>
              </AriaDisclosureButton>
            </Heading>
            <DisclosurePanel className="px-4 pb-4 text-primary">
          <dl className="m-0 grid grid-cols-1 gap-3 py-2" data-testid="qr-display-manual">
            <div className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(8rem,max-content)_1fr] sm:gap-2">
              <dt className="text-sm font-semibold text-tertiary">{t("payment.iban")}</dt>
              <dd className="m-0 flex flex-wrap items-center justify-between gap-2 font-mono text-[0.9375rem] text-primary sm:flex-nowrap">
                <span data-testid="qr-display-iban-value">{iban}</span>
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onPress={() => handleCopy(iban, t("payment.iban"))}
                  data-testid="qr-display-copy-iban"
                  aria-label={t("payment.copy.ariaLabel", { field: t("payment.iban") })}
                >
                  {t("payment.copy.button")}
                </Button>
              </dd>
            </div>
            <div className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(8rem,max-content)_1fr] sm:gap-2">
              <dt className="text-sm font-semibold text-tertiary">{t("payment.vs")}</dt>
              <dd className="m-0 flex flex-wrap items-center justify-between gap-2 font-mono text-[0.9375rem] text-primary sm:flex-nowrap">
                <span data-testid="qr-display-vs-value">{variableSymbol}</span>
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onPress={() => handleCopy(variableSymbol, t("payment.vs"))}
                  data-testid="qr-display-copy-vs"
                  aria-label={t("payment.copy.ariaLabel", { field: t("payment.vs") })}
                >
                  {t("payment.copy.button")}
                </Button>
              </dd>
            </div>
            <div className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(8rem,max-content)_1fr] sm:gap-2">
              <dt className="text-sm font-semibold text-tertiary">{t("payment.amount")}</dt>
              <dd className="m-0 flex flex-wrap items-center justify-between gap-2 font-mono text-[0.9375rem] text-primary sm:flex-nowrap">
                <span data-testid="qr-display-amount-value">{amountKc} Kč</span>
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onPress={() => handleCopy(amountKc, t("payment.amount"))}
                  data-testid="qr-display-copy-amount"
                  aria-label={t("payment.copy.ariaLabel", { field: t("payment.amount") })}
                >
                  {t("payment.copy.button")}
                </Button>
              </dd>
            </div>
            {message && (
              <div className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(8rem,max-content)_1fr] sm:gap-2">
                <dt className="text-sm font-semibold text-tertiary">{t("payment.message")}</dt>
                <dd className="m-0 flex flex-wrap items-center justify-between gap-2 font-mono text-[0.9375rem] text-primary sm:flex-nowrap">
                  <span>{message}</span>
                </dd>
              </div>
            )}
          </dl>
            </DisclosurePanel>
          </>
        )}
      </AriaDisclosure>
    </section>
  );
}

/** Helpers exportované pro unit tests. */
export const __testing = {
  base64ToPngBlob,
  distance,
  clamp,
  MIN_SCALE,
  MAX_SCALE,
};
